import React, { useEffect, useState } from "react";
import style from "./PurchaseOrder.module.css";
import MenuBar from "./MenuBar";
import axios from "axios";
import excelIcon from "./assets/excel.png";
import pdfIcon from "./assets/pdf.png";
import moment from "moment";
import DateRangePicker from "./DateRangePicker";
import { toast } from "react-toastify";
import { API_BASE_URL } from "./config.js";
import HeadingBar from "./HeadingBar.js";

/* ---------- Helpers ---------- */
const formatDateForAPI = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

/* ---------- Excel Download ---------- */
const handleDownloadCsv = async (po) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/OpenPO/DownloadExcel?DocNum=${po.DocEntry}`,
    );

    if (!response.ok) throw new Error("Failed to download CSV");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `OpenPO_${po.DocNum}_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    toast.error("Could not download Excel.");
  }
};

/* ---------- Component ---------- */
const PurchaseOrder = () => {
  const vendorDataRaw = sessionStorage.getItem("vendordata");
  const vendorInfo = vendorDataRaw ? JSON.parse(vendorDataRaw) : null;
  const vendorCode = vendorInfo?.CardCode || "";
  const isEmbedded = sessionStorage.getItem("mode") === "embedded";
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedValue, setSelectedValue] = useState("O");
  const [poSearchText, setPoSearchText] = useState("");
  const [poData, setPoData] = useState([]);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();

  /* 🔴 PDF waiting state (row-level) */
  const [pdfDownloadingDocEntry, setPdfDownloadingDocEntry] = useState(null);

  /* ---------- PDF Download with Waiting ---------- */
  const handleDownloadPDF = async (po) => {
    try {
      setPdfDownloadingDocEntry(po.DocEntry);

      const response = await fetch(
        `${API_BASE_URL}/OpenPO/DownloadSamplePDF?DocNum=${po.DocEntry}`,
      );

      if (!response.ok) throw new Error("Failed to download PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `PO_${po.DocNum}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Could not download PDF.");
    } finally {
      setPdfDownloadingDocEntry(null);
    }
  };

  /* ---------- API Calls ---------- */
  const fetchPOData = async (from, to, status, location) => {
    if (!vendorCode) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/OpenPO/Get?VendorCode=${vendorCode}&FromDt=${formatDateForAPI(
          from,
        )}&ToDt=${formatDateForAPI(to)}&Status=${status}&Location=${location}`,
      );
      setPoData(res.data);
    } catch {
      toast.error("Failed to fetch PO data.");
    }
  };

  const fetchPODataDocNo = async (docNum) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/OpenPO/GetPoNo?VendorCode=${vendorCode}&DocNum=${docNum}`,
      );
      setPoData(res.data);
    } catch {
      toast.error("Failed to fetch PO.");
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/location`);
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error("Failed to fetch locations.");
    }
  };

  /* ---------- Events ---------- */
  const handleSearch = () => {
    poSearchText
      ? fetchPODataDocNo(poSearchText)
      : fetchPOData(fromDate, toDate, selectedValue, selectedLocation);
  };

  const handleDateChange = (start, end) => {
    setFromDate(start);
    setToDate(end);
    fetchPOData(start, end, selectedValue, selectedLocation);
  };

  /* ---------- Initial Load ---------- */
  useEffect(() => {
    fetchLocations();
    const start = moment().startOf("quarter").format("YYYY-MM-DD");
    const end = moment().endOf("quarter").format("YYYY-MM-DD");
    setFromDate(start);
    setToDate(end);
    fetchPOData(start, end, selectedValue, selectedLocation);
  }, [vendorCode]);

  /* ---------- UI ---------- */
  return (
    <div className={style.PurchaseOrderDetails}>
      {!isEmbedded && (
        <div className={style.HeadingBarContainer}>
          <HeadingBar />
        </div>
      )}

      <div className={style.PurchaseOrderDetailsContainer}>
        <div className={style.MenuBarContainer}>
          <MenuBar />
        </div>

        <div className={style.MainPurchaseOrderDetailsContainer}>
          <div className={style.MenuNameDetails}>Purchase Order Details</div>

          <div className={style.filterArea}>
            <div className={style.pofilter}>
              <div>PO No.</div>
              <input
                value={poSearchText}
                onChange={(e) => setPoSearchText(e.target.value)}
              />

              <div>Status</div>
              <select
                value={selectedValue}
                onChange={(e) => {
                  setSelectedValue(e.target.value);
                  fetchPOData(
                    fromDate,
                    toDate,
                    e.target.value,
                    selectedLocation,
                  );
                }}
              >
                <option value="O">Open</option>
                <option value="C">Closed</option>
              </select>

              <div>Location</div>
              <select
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  fetchPOData(fromDate, toDate, selectedValue, e.target.value);
                }}
              >
                <option value="">All</option>
                {locations.map((l) => (
                  <option key={l.Code} value={l.Code}>
                    {l.Location}
                  </option>
                ))}
              </select>
            </div>

            <div className={style.datearea}>
              <DateRangePicker
                onDateChange={handleDateChange}
                startDate={fromDate}
                endDate={toDate}
              />
              <button onClick={handleSearch}>Search</button>
            </div>
          </div>

          <div className={style.MainArea}>
            <div className={style.potable}>
              <table>
                <thead>
                  <tr>
                    <td>PO #</td>
                    <td>PO Date</td>
                    <td>Del Date</td>
                    <td>PO Type</td>
                    <td>Vendor Ref #</td>
                    <td>Location</td>
                    <td>Taxable Amount</td>
                    <td>Tax Amount</td>
                    <td>Total Amount</td>
                    <td>Status</td>
                    <td>Action</td>
                  </tr>
                </thead>

                <tbody>
                  {poData.length ? (
                    poData.map((po, i) => (
                      <tr key={i}>
                        <td>{po.DocNum}</td>
                        <td>{po.PODate}</td>
                        <td>{po.DeliveryDate}</td>
                        <td>{po.POType}</td>
                        <td>{po.VendRefNo}</td>
                        <td>{po.Location}</td>
                        <td className="text-end amount-cell">
                          {po.DocCur}{" "}
                          {Number(po.TaxableAmt).toLocaleString("en-IN")}
                        </td>
                        <td className="text-end amount-cell">
                          {po.DocCur}{" "}
                          {Number(po.TaxAmt).toLocaleString("en-IN")}
                        </td>
                        <td className="text-end amount-cell">
                          {po.DocCur}{" "}
                          {Number(po.DocTotal).toLocaleString("en-IN")}
                        </td>
                        <td>{po.Status}</td>

                        <td
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 10,
                          }}
                        >
                          <img
                            src={excelIcon}
                            alt="Excel"
                            style={{ width: 20, cursor: "pointer" }}
                            onClick={() => handleDownloadCsv(po)}
                          />

                          {pdfDownloadingDocEntry === po.DocEntry ? (
                            <span style={{ fontSize: 12, color: "#666" }}>
                              Downloading...
                            </span>
                          ) : (
                            <img
                              src={pdfIcon}
                              alt="PDF"
                              style={{ width: 20, cursor: "pointer" }}
                              onClick={() => handleDownloadPDF(po)}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        No PO Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrder;
